import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CampaignDetailsReviewComponent } from '../../../../../components/forms/campaignreviews/campaign-details-review/campaign-details-review.component';
import { RecipientsReviewComponent } from '../../../../../components/forms/campaignreviews/recipients/recipients-review.component';
import { MessageReviewComponent } from '../../../../../components/forms/campaignreviews/message-review/message-review.component';
import { ScheduleReviewComponent } from '../../../../../components/forms/campaignreviews/schedule-review/schedule-review.component';
import { CampaignDetailsService } from '../../../../../services/campaign/campaign-details.service';
import { RecipientsService } from '../../../../../services/campaign/recipients.service';
import { MessagesService } from '../../../../../services/campaign/messages.service';
import { ScheduleService } from '../../../../../services/campaign/schedule.service';
import { CampaignsService } from '../../../../../services/campaign/campaigns-api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignStepperService } from '../../../../../services/campaign/campaign-stepper.service';
import { CancelCampaignService } from '../../../../../services/campaign/cancel-campaign.service';
import { UtilityService } from '../../../../../services/utility.service';
import { FeedbackCardComponent } from '../../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { UserProfileService } from '../../../../../services/user/user-profile.service';
import { extractMessageAttributes } from '../../../../../../utils/utils';
import { formatCount, smsParts } from '../../../../../../utils/sms';
import { WizardAudienceService } from '../wizard-audience.service';
import { CampaignDuplicateService } from '../campaign-duplicate.service';
import { scheduleMoment } from '../../campaign-list/campaign-facts';

/** Something that stops the launch, and the step that fixes it. */
interface Problem {
  text: string;
  step: string;
  action: string;
}

@Component({
  selector: 'app-review',
  standalone: true,
  imports: [
    CampaignDetailsReviewComponent,
    RecipientsReviewComponent,
    MessageReviewComponent,
    ScheduleReviewComponent,
    FeedbackCardComponent,
  ],
  templateUrl: './review.component.html',
  styleUrl: './review.component.css',
})
export class ReviewComponent implements OnInit, OnDestroy {
  campaignDetailsService = inject(CampaignDetailsService);
  recipientsService = inject(RecipientsService);
  messagesService = inject(MessagesService);
  scheduleService = inject(ScheduleService);
  campaignsService = inject(CampaignsService);
  campaignStepperService = inject(CampaignStepperService);
  cancelCampaignService = inject(CancelCampaignService);
  utilityService = inject(UtilityService);
  userService = inject(UserProfileService);
  audience = inject(WizardAudienceService);
  private duplicator = inject(CampaignDuplicateService);
  router = inject(Router);
  private route = inject(ActivatedRoute);
  success: boolean = false;
  noContact: boolean = false;
  /** Name kept for the success view, which outlives the reset wizard state. */
  launchedName = '';
  private successTimeoutId: ReturnType<typeof setTimeout> | undefined;

  readonly formatCount = formatCount;

  get organizationId() {
    return this.userService.organizationId;
  }

  /** Every scheduled send across all messages. */
  get totalSends(): number {
    return this.messagesService.savedMessages.reduce((sum, message) => sum + this.sendsFor(message.id), 0);
  }

  /** SMS the whole campaign costs at today's audience: Σ parts × sends × people. */
  get totalUnits(): number | null {
    if (this.audience.count === null) return null;
    return this.messagesService.savedMessages.reduce(
      (sum, message) => sum + smsParts(message.body).parts * this.sendsFor(message.id) * (this.audience.count ?? 0),
      0
    );
  }

  /** Personalized fields make lengths, and so the estimate, vary per person. */
  get unitsEstimated(): boolean {
    return this.messagesService.savedMessages.some((message) => smsParts(message.body).estimated);
  }

  /** What still stops the launch. Empty when everything holds. */
  get problems(): Problem[] {
    const problems: Problem[] = [];
    const details = this.campaignDetailsService.campaignDetails;
    const goals = (details.goals ?? []).filter((goal) => !!String(goal ?? '').trim());
    if (!details.campaignName || !details.description || !goals.length) {
      problems.push({ text: 'The name, description or goals are missing.', step: 'campaign-details', action: 'Add details' });
    }

    const { organizations, tags, segments } = this.recipientsService.recipients;
    if (!organizations.length && !tags.length && !segments.length) {
      problems.push({ text: 'No recipients are chosen.', step: 'recipients', action: 'Choose recipients' });
    } else if (this.audience.count === 0) {
      problems.push({
        text: 'The chosen groups have no contacts, so nobody would receive it.',
        step: 'recipients',
        action: 'Change recipients',
      });
    }

    if (!this.messagesService.savedMessages.length) {
      problems.push({ text: 'There are no messages to send.', step: 'messages', action: 'Add a message' });
    }

    const { campaignStart, campaignEnd } = this.scheduleService.campaignSchedule;
    if (!campaignStart || !campaignEnd) {
      problems.push({ text: 'The campaign dates are not set.', step: 'schedule', action: 'Set dates' });
    }

    const unscheduled = this.messagesService.savedMessages.filter((message) => !this.sendsFor(message.id));
    if (unscheduled.length === 1) {
      problems.push({
        text: `“${unscheduled[0].subject || 'Untitled message'}” has no send time.`,
        step: 'schedule',
        action: 'Add a send time',
      });
    } else if (unscheduled.length > 1) {
      problems.push({ text: `${unscheduled.length} messages have no send time.`, step: 'schedule', action: 'Add send times' });
    }

    const now = Date.now();
    const past = this.scheduleService.messageSchedule
      .flatMap((schedule) => schedule.messageDates ?? [])
      .filter((row) => {
        const at = scheduleMoment(row.date, row.time, row.timezone);
        return !!at && at.getTime() <= now;
      }).length;
    if (past) {
      problems.push({
        text: past === 1 ? 'One send time has already passed.' : `${past} send times have already passed.`,
        step: 'schedule',
        action: 'Change send times',
      });
    }
    return problems;
  }

  goToStep(step: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
    });
  }

  submitCampaign() {
    if (this.campaignsService.isLoading || this.problems.length) return;

    const campaign = {
      name: this.campaignDetailsService.campaignDetails.campaignName,
      description: this.campaignDetailsService.campaignDetails.description,
      startDate: this.scheduleService.campaignSchedule.campaignStart,
      endDate: this.scheduleService.campaignSchedule.campaignEnd,
      organisationId: this.organizationId,
      /** Used server-side to interpret scheduled date/time in the same zone as the browser. */
      scheduleTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      createdAt: new Date(),
      batchContactsPayload: {
        tagUuids: this.recipientsService.recipients.tags.map((tag) => tag.uuid),
        segmentUuids: this.recipientsService.recipients.segments.map((segment) => segment.uuid),
        companyUuids: this.recipientsService.recipients.organizations.map((organization) => organization.uuid),
        blackListContactsUuids: [],
      },
      engagementObjectives: this.campaignDetailsService.campaignDetails.goals.map((goal) => {
        return { name: goal };
      }),
      engagementOrganizations: this.recipientsService.recipients.organizations.map((organization) => {
        return {
          organizationId: organization.uuid,
          name: organization.name,
          organizationIntId: organization.id,
        };
      }),
      engagementRecipientSegments: this.recipientsService.recipients.segments.map((segment) => {
        return {
          name: segment.name,
          segmentId: segment.id,
          segmentUuid: segment.uuid,
        };
      }),
      engagementRecipientTags: this.recipientsService.recipients.tags.map((tag) => {
        return { name: tag.name, tagUuid: tag.uuid, tagIntId: tag.id };
      }),
      engagementMessages: this.messagesService.savedMessages.map((message) => {
        // contact attributes embedded as `{{attribute}}` placeholders in the body
        const attributes = extractMessageAttributes(message.body);
        return {
          messageSubject: message.subject,
          messageTemplate: 'sampleTemplate',
          content: message.body,
          senderId: message.senderId,
          from: message.senderId,
          hasAttribute: attributes.length > 0,
          attributes,
          engagementMessageSchedules: this.scheduleService.messageSchedule
            .find((ms) => message.id === ms?.message?.id)
            ?.messageDates.map((date) => {
              return {
                scheduleDate: date.date,
                scheduleTime: date.time,
                scheduleTimeZone: date.timezone,
              };
            }),
        };
      }),
    };

    this.campaignsService.saveCampaign(campaign).subscribe(() => {
      this.launchedName = this.campaignDetailsService.campaignDetails.campaignName ?? '';
      this.success = true;
      // Clear the wizard now rather than at the redirect: leaving the success
      // view early must not leave the launched campaign ready to launch again.
      this.resetWizard();
      if (this.successTimeoutId) {
        clearTimeout(this.successTimeoutId);
      }
      this.successTimeoutId = setTimeout(() => {
        this.router.navigate(['organization/campaigns']);
        this.success = false;
        this.successTimeoutId = undefined;
      }, 2400);
    });
  }

  private resetWizard() {
    this.campaignDetailsService.reset();
    this.recipientsService.reset();
    this.messagesService.resetSavedMessages();
    this.scheduleService.reset();
    this.campaignStepperService.reset();
    this.duplicator.clear();
    this.audience.reset();
  }

  ngOnInit() {
    // Contacts may have been added or removed since the last count, so the
    // figure the launch is checked against is always a fresh one.
    this.audience.refresh(true);
  }

  ngOnDestroy() {
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
  }

  private sendsFor(messageId: string): number {
    return this.scheduleService.messageSchedule
      .filter((schedule) => schedule.message?.id === messageId)
      .reduce((sum, schedule) => sum + (schedule.messageDates?.length ?? 0), 0);
  }
}
